<template>
  <div class="ivc-shell">
    <!-- Header -->
    <header class="ivc-header">
      <div class="ivc-header-left">
        <div class="ivc-icon-badge">
          <i class="fa fa-user-plus"></i>
        </div>
        <div>
          <div class="ivc-title-row">
            <h2 class="ivc-title">{{ $t('customerMemberCard.assignCustomer') }}</h2>
            <span class="ivc-chip" v-if="edit_data.serial_card">
              <span class="ivc-chip-dot"></span>{{ edit_data.serial_card }}
            </span>
          </div>
          <p class="ivc-subtitle">{{ headerSubtitle }}</p>
        </div>
      </div>
      <button type="button" class="ivc-close" aria-label="Close" :disabled="assigning" @click.prevent="$emit('cancel')">
        <i class="fa fa-times"></i>
      </button>
    </header>

    <!-- Scrollable body -->
    <div class="ivc-body">
      <section class="ivc-meta-card">
        <div class="ivc-meta-grid">
          <div class="ivc-meta-col">
            <span class="ivc-meta-label">{{ $t('customerMemberCard.customer') }}</span>
            <span class="ivc-meta-value">{{ edit_data.customer_name || '-' }}</span>
          </div>
          <div class="ivc-meta-col ivc-meta-col-right">
            <span class="ivc-meta-label">{{ $t('customerMemberCard.phoneNumber') }}</span>
            <span class="ivc-meta-value ivc-mono">{{ edit_data.contact_number || '-' }}</span>
          </div>

          <div class="ivc-meta-divider"></div>

          <div class="ivc-meta-col">
            <span class="ivc-meta-label">{{ $t('customerMemberCard.serialCard') }}</span>
            <span class="ivc-meta-value-row">
              <span class="ivc-meta-value ivc-mono ivc-bold">{{ edit_data.serial_card }}</span>
            </span>
          </div>
          <div class="ivc-meta-col ivc-meta-col-right">
            <span class="ivc-meta-label">{{ $t('customerMemberCard.cardType') }}</span>
            <span class="ivc-meta-value">{{ edit_data.card_types || '-' }}</span>
          </div>

          <div class="ivc-meta-divider"></div>

          <template v-if="edit_data.promotion_type_id === 2">
            <div class="ivc-meta-col">
              <span class="ivc-meta-label">{{ $t('customerMemberCard.promotion') }}</span>
              <span class="ivc-meta-value">{{ edit_data.package_name || '-' }}</span>
            </div>
            <div class="ivc-meta-col ivc-meta-col-right">
              <span class="ivc-meta-label">{{ $t('customerMemberCard.maxTimeQuota') }}</span>
              <span class="ivc-meta-value ivc-mono ivc-bold">{{ edit_data.max_times }}</span>
            </div>
          </template>
          <div class="ivc-meta-col ivc-meta-col-span" v-else>
            <span class="ivc-meta-label">{{ $t('customerMemberCard.maxTimeQuota') }}</span>
            <span class="ivc-meta-value ivc-mono ivc-bold">{{ edit_data.max_times }}</span>
          </div>
        </div>
      </section>

      <div class="mc-mode-tabs">
        <button type="button" class="mc-mode-btn" :class="{ 'mc-mode-btn-active': customerMode === 'new' }"
          @click="setCustomerMode('new')">
          {{ $t('customerMemberCard.newCustomer') }}
        </button>
        <button type="button" class="mc-mode-btn" :class="{ 'mc-mode-btn-active': customerMode === 'old' }"
          @click="setCustomerMode('old')">
          {{ $t('customerMemberCard.existingCustomer') }}
        </button>
      </div>

      <!-- new customer -->
      <template v-if="customerMode === 'new'">
        <a-alert type="info" show-icon :message="$t('customerMemberCard.assignCustomerHint')" />

        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-md-12">
              <label class="ts-text-xs required">{{ $t('customerProfile.customerType') }}</label>
              <a-select v-model="model.cus_type_id" :options="listType" :dropdown-style="{ zIndex: 2200 }"
                class="ts-w-full" :class="{ 'is-invalid': errors.has('cus_type_id') }"></a-select>
              <div class="validate ts-text-xs" v-if="errors.has('cus_type_id')">{{ errors.first('cus_type_id') }}
              </div>
            </div>
          </div>

          <div class="row ts-mt-2">
            <div class="col-md-12">
              <label class="ts-text-xs required">{{ $t('customerProfile.customerName') }}</label>
              <input v-model.trim="model.customer_name" type="text" class="form-control"
                :class="{ 'is-invalid': errors.has('customer_name') }" />
              <div class="invalid-feedback ts-text-xs" v-if="errors.has('customer_name')">
                {{ errors.first('customer_name') }}
              </div>
            </div>
          </div>

          <div class="row ts-mt-2">
            <div class="col-md-12">
              <a-radio-group v-model="model.gender" class="ts-p-3">
                <a-radio value="Male">Male</a-radio>
                <a-radio value="Female">Female</a-radio>
                <a-radio value="Other">Other</a-radio>
              </a-radio-group>
              <div class="validate ts-text-xs" v-if="errors.has('gender')">{{ errors.first('gender') }}</div>
            </div>
          </div>

          <div class="row ts-mt-2">
            <div class="col-md-12">
              <label class="ts-text-xs required">{{ $t('customerProfile.phoneNumber') }}</label>
              <input v-model.trim="model.contact_number" type="text" class="form-control"
                :class="{ 'is-invalid': errors.has('contact_number') }" />
              <div class="invalid-feedback ts-text-xs" v-if="errors.has('contact_number')">
                {{ errors.first('contact_number') }}
              </div>
            </div>
          </div>

          <div class="row ts-mt-3">
            <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2">
              <ts-button :disabled="assigning" @click.prevent="$emit('cancel')">{{ $t('cancel') }}</ts-button>
              <ts-button color="primary" :waiting="assigning" @click.prevent="onSave">{{ $t('save') }}</ts-button>
            </div>
          </div>
        </ts-loading-banner>
      </template>

      <!-- existing customer -->
      <template v-else>
        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-md-12">
              <label class="ts-text-xs required">{{ $t('customerMemberCard.customer') }}</label>
              <a-select v-model="selectedCustomerId" :options="customerOptions" show-search allow-clear
                option-filter-prop="children" :dropdown-style="{ zIndex: 2200 }" class="ts-w-full"
                :class="{ 'is-invalid': errors.has('customer_id') }" :placeholder="$t('select')"></a-select>
              <div class="validate ts-text-xs" v-if="errors.has('customer_id')">{{ errors.first('customer_id') }}
              </div>
            </div>
          </div>

          <div class="row ts-mt-3">
            <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2">
              <ts-button :disabled="assigning" @click.prevent="$emit('cancel')">{{ $t('cancel') }}</ts-button>
              <ts-button color="primary" :waiting="assigning" @click.prevent="onAssignExisting">
                {{ $t('customerMemberCard.assignCustomer') }}
              </ts-button>
            </div>
          </div>
        </ts-loading-banner>
      </template>
    </div>

    <Spin size="large" fix v-if="assigning"></Spin>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'assignCustomerToMemberCard',
  data() {
    return {
      customerMode: 'new',
      selectedCustomerId: null,
      loading: false,
      assigning: false,
      errors: new Errors(),
      model: {
        cus_type_id: 2,
        customer_name: null,
        gender: null,
        contact_number: null,
        // Non-nullable on the backend DTO — must be sent even though this
        // trimmed form doesn't surface them as inputs.
        is_general_customer: false,
        is_membership: false,
        is_allow_update_gps_location: false,
        membership_discount_rate: 0,
        // approve_status is NOT NULL on setup_customer_profiles; 2 = Approved
        // (no pending approval workflow) for a quick-created customer.
        approve_status: 2
      }
    }
  },
  created() {
    this.fetchResources()
  },
  computed: {
    ...mapState('customer/customerMemberCard', ['edit_data', 'formViewData']),
    headerSubtitle() {
      return this.customerMode === 'new'
        ? this.$t('customerMemberCard.assignCustomerHint')
        : this.$t('customerMemberCard.selectCustomerHint')
    },
    listType() {
      return this.$store.state.customer.profile.customerType.map(el => ({
        value: el.cus_type_id,
        label: el.customer_type
      }))
    },
    customerOptions() {
      if (this.formViewData.customer && this.formViewData.customer.length) {
        return this.formViewData.customer.map(el => ({
          label:
            'Code: ' +
            el.customer_code +
            ' | Customer: ' +
            el.customer_name +
            ' | Contact: ' +
            el.contact_number +
            ' | Gender: ' +
            el.gender,
          value: el.customer_id
        }))
      }
      return []
    }
  },
  methods: {
    ...mapActions('customer/customerMemberCard', ['getFormViewData']),
    fetchResources() {
      this.loading = true
      this.$store.dispatch('customer/profile/getCustomerType').finally(() => {
        this.loading = false
      })
    },
    setCustomerMode(mode) {
      this.customerMode = mode
      this.errors = new Errors()
      if (mode === 'old' && !this.customerOptions.length) {
        this.fetchExistingCustomers()
      }
    },
    fetchExistingCustomers() {
      this.loading = true
      this.getFormViewData({ params: undefined, index: undefined }).finally(() => {
        this.loading = false
      })
    },
    onAssignExisting() {
      if (!this.selectedCustomerId) {
        this.errors = new Errors({ customer_id: ['Please select a customer.'] })
        return
      }
      this.errors = new Errors()
      const selected = (this.formViewData.customer || []).find(el => el.customer_id === this.selectedCustomerId)
      this.assigning = true
      this.assignToCard({
        customer_id: this.selectedCustomerId,
        customer_name: selected ? selected.customer_name : null,
        contact_number: selected ? selected.contact_number : null
      })
    },
    onSave() {
      this.assigning = true
      this.errors = new Errors()
      this.$store
        .dispatch('customer/profile/store', this.model)
        .then(response => this.assignToCard(response.data))
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
          this.assigning = false
        })
    },
    assignToCard(customer) {
      this.$store
        .dispatch('customer/customerMemberCard/assignCustomer', {
          id: this.edit_data.customer_member_card_id,
          data: { customer_id: customer.customer_id }
        })
        .then(() => {
          this.$emit('assigned', {
            customer_id: customer.customer_id,
            customer_name: customer.customer_name,
            contact_number: customer.contact_number
          })
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.assigning = false
        })
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600;700&display=swap');

.ivc-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 96vh;
  border-radius: 14px;
  overflow: hidden;
  font-family: 'Manrope', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header */
.ivc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
}

.ivc-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ivc-icon-badge {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  color: #3182ce;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.ivc-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ivc-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.01em;
}

.ivc-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.ivc-chip-dot {
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 9999px;
  background: #059669;
}

.ivc-subtitle {
  font-size: 12px;
  color: #64748b;
  margin: 2px 0 0;
}

.ivc-close {
  border: none;
  background: transparent;
  color: #94a3b8;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
  outline: none;
}

.ivc-close:hover {
  color: #334155;
  background: #f1f5f9;
}

.ivc-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Body */
.ivc-body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
}

.ivc-body>*+* {
  margin-top: 14px;
}

/* Metadata card */
.ivc-meta-card {
  background: rgba(248, 250, 252, 0.8);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
}

.ivc-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  row-gap: 10px;
}

.ivc-meta-col {
  display: flex;
  flex-direction: column;
}

.ivc-meta-col-right {
  align-items: flex-end;
  text-align: right;
}

.ivc-meta-col-span {
  grid-column: 1 / -1;
}

.ivc-meta-label {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
}

.ivc-meta-value {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  margin-top: 2px;
}

.ivc-meta-value-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.ivc-meta-divider {
  grid-column: 1 / -1;
  border-top: 1px solid rgba(226, 232, 240, 0.7);
  margin: 1px 0;
}

.ivc-mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

.ivc-bold {
  font-weight: 700;
}

/* Step 1 mode tabs (assign customer) */
.mc-mode-tabs {
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}

.mc-mode-btn {
  border: none;
  background: #fff;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  cursor: pointer;
}

.mc-mode-btn+.mc-mode-btn {
  border-left: 1px solid #d1d5db;
}

.mc-mode-btn-active {
  background: #2563eb;
  color: #fff;
}
</style>
